import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface StatusOption {
  value: string;
  label: string;
}

interface StatusSelectorProps {
  options: StatusOption[];
  selectedOptions: string[];
  onChange: (options: string[]) => void;
  variant?: 'default' | 'compact';
  className?: string;
}

export default function StatusSelector({
  options,
  selectedOptions,
  onChange,
  variant = 'default',
  className,
}: StatusSelectorProps) {
  const isCompact = variant === 'compact';

  const toggleOption = (value: string) => {
    if (selectedOptions.includes(value)) {
      onChange(selectedOptions.filter((v) => v !== value));
    } else {
      onChange([...selectedOptions, value]);
    }
  };

  return (
    <div className={cn("grid gap-1.5 grid-cols-1 sm:grid-cols-2", className)}>
      {options.map((option) => {
        const isSelected = selectedOptions.includes(option.value);
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => toggleOption(option.value)}
            className={cn(
              'flex items-center gap-2.5 rounded-lg text-left transition-all duration-150 cursor-pointer group/item min-h-[44px]',
              isCompact ? 'px-2.5 py-2.5' : 'px-3 py-2.5',
              isSelected
                ? 'bg-primary/8 ring-1 ring-primary/30'
                : 'bg-zinc-900/40 hover:bg-zinc-800/50 ring-0 sm:ring-1 ring-zinc-800/60'
            )}
          >
            <div
              className={cn(
                'w-4 h-4 rounded flex items-center justify-center shrink-0 transition-all duration-150',
                isSelected
                  ? 'bg-primary border-primary'
                  : 'border border-zinc-600 group-hover/item:border-zinc-400'
              )}
            >
              {isSelected && <Check size={10} strokeWidth={3} className="text-black" />}
            </div>
            <span
              className={cn(
                'text-xs leading-tight truncate transition-colors duration-150',
                isSelected
                  ? 'text-colorText font-medium'
                  : 'text-zinc-400 group-hover/item:text-zinc-300'
              )}
            >
              {option.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}
